import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { BRANCH_META } from "@/lib/branches";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Sparkles,
    Users,
    Lightbulb,
    RefreshCw,
    Send,
    X,
    Clock,
    Compass,
    Rocket,
} from "lucide-react";

export default function Matchmaker() {
    const [state, setState] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [inviting, setInviting] = useState(null); // {user_id, name, project_title?}
    const [inviteMsg, setInviteMsg] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [loadError, setLoadError] = useState(null);

    const load = () => {
        setLoadError(null);
        api.get("/matchmaker/me")
            .then((r) => setState(r.data))
            .catch((e) => {
                // Render something instead of leaving the page blank on a null state.
                setState({ cache: null, can_refresh: true, seconds_until_refresh: 0 });
                setLoadError(
                    e?.response?.data?.detail ||
                    "Matchmaker is unavailable right now. Tap refresh or come back in a minute."
                );
            });
    };

    useEffect(load, []);

    const refresh = async () => {
        setRefreshing(true);
        setError(null);
        try {
            const { data } = await api.post("/matchmaker/refresh");
            setState(data);
            toast.success("Fresh matches assembled.");
        } catch (e) {
            const detail = e?.response?.data?.detail || "Matchmaker unavailable.";
            setError(detail);
            toast.error(detail);
        } finally {
            setRefreshing(false);
        }
    };

    const openInvite = (peer, projectTitle = null) => {
        const base = projectTitle
            ? `Hi ${peer.name}, I saw the matchmaker suggested we team up on "${projectTitle}". Want to try it?`
            : `Hi ${peer.name}, the Atlas matchmaker suggested we might make a good team — `;
        setInviting({ ...peer, project_title: projectTitle });
        setInviteMsg(base);
    };

    const sendInvite = async (e) => {
        e.preventDefault();
        if (!inviteMsg.trim() || !inviting) return;
        setBusy(true);
        try {
            await api.post("/notes/send", {
                to_user_id: inviting.user_id,
                message: inviteMsg,
            });
            toast.success(`Invite sent to ${inviting.name}.`);
            setInviting(null);
            setInviteMsg("");
        } catch {
            toast.error("Couldn't send that invite.");
        } finally {
            setBusy(false);
        }
    };

    if (!state) {
        return (
            <div className="min-h-screen w-full">
                <TopBar />
                <div className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-8 text-center text-slate-500 font-display">
                    Loading matchmaker…
                </div>
            </div>
        );
    }

    const cache = state.cache;
    const cooldown = state.seconds_until_refresh || 0;
    const cooldownMin = Math.ceil(cooldown / 60);

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <p className="chip" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}>
                    <Sparkles className="w-3 h-3" /> Collaboration Matchmaker
                </p>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none">
                    Find your co-explorers.
                </h1>
                <p className="text-slate-600 mt-2 max-w-2xl">
                    Claude scans your ignited disciplines, XP, and mastery, then pairs you with real
                    peers who complement you — plus project ideas you could ship together.
                </p>

                {loadError && (
                    <div className="mt-6 crystal rounded-3xl p-5" data-testid="matchmaker-load-error">
                        <div className="flex flex-wrap items-start gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="font-display font-bold text-slate-900">
                                    We hit a snag loading this page.
                                </div>
                                <div className="text-sm text-slate-600 mt-1">{loadError}</div>
                            </div>
                            <button
                                onClick={load}
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="matchmaker-load-retry"
                            >
                                Try again
                            </button>
                        </div>
                        {loadError.toLowerCase().includes("authenticat") && (
                            <div className="mt-3 pt-3 border-t border-slate-200/70 text-sm text-slate-600">
                                Still stuck?{" "}
                                <button
                                    onClick={async () => {
                                        try { await api.post("/auth/logout"); } catch {}
                                        window.location.href = "/";
                                    }}
                                    className="text-humanities font-display font-bold underline underline-offset-2"
                                    data-testid="matchmaker-signin-again"
                                >Sign in again</button>{" "}to reset your session.
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button
                        onClick={refresh}
                        disabled={refreshing || !state.can_refresh}
                        className="arcade-btn"
                        data-variant="primary"
                        data-testid="matchmaker-refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                        {refreshing ? "Assembling…" : cache ? "Refresh matches" : "Assemble matches"}
                    </button>
                    {!state.can_refresh && cooldown > 0 && (
                        <span className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="matchmaker-cooldown">
                            <Clock className="w-3 h-3" /> Ready again in ~{cooldownMin} min
                        </span>
                    )}
                    {cache?.generated_at && (
                        <span className="text-xs text-slate-400 ml-auto font-mono">
                            Last run {new Date(cache.generated_at).toLocaleString()}
                        </span>
                    )}
                </div>

                {error && (
                    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900" data-testid="matchmaker-error">
                        {error}
                    </div>
                )}

                {!cache && !error && !refreshing && (
                    <div className="mt-10 crystal rounded-3xl p-10 text-center">
                        <Rocket className="w-8 h-8 mx-auto text-slate-400" />
                        <h2 className="font-display font-bold text-2xl tracking-tighter text-slate-900 mt-3">
                            Ready to find your team?
                        </h2>
                        <p className="text-slate-600 mt-1 max-w-md mx-auto">
                            Ignite at least one beacon, then tap <strong>Assemble matches</strong> — the AI
                            will look at your discipline mix and pull the best peers + project ideas for you.
                        </p>
                        <Link to="/atlas" className="arcade-btn mt-5 inline-flex" data-variant="ghost" data-testid="matchmaker-goto-atlas">
                            <Compass className="w-4 h-4" /> Open the Atlas
                        </Link>
                    </div>
                )}

                {cache && (
                    <div className="mt-8 grid md:grid-cols-2 gap-6">
                        {/* Suggested peers */}
                        <section data-testid="matchmaker-peers">
                            <div className="flex items-center gap-2 mb-3">
                                <Users className="w-4 h-4" color={BRANCH_META.sciences.color} />
                                <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                                    Suggested peers
                                </h2>
                            </div>
                            {cache.peers.length === 0 && (
                                <div className="crystal rounded-2xl p-5 text-sm text-slate-600">
                                    No matched peers yet — you're on a frontier. Once other explorers ignite
                                    beacons in your disciplines, they'll show up here.
                                </div>
                            )}
                            <div className="space-y-3">
                                {cache.peers.map((p, i) => (
                                    <motion.div
                                        key={p.user_id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.03 }}
                                        className="crystal rounded-2xl p-5"
                                        data-testid={`match-peer-${p.user_id}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div
                                                className="w-11 h-11 rounded-full flex items-center justify-center font-display font-bold text-white text-sm flex-shrink-0"
                                                style={{ background: `linear-gradient(135deg, ${BRANCH_META.arts.color}, ${BRANCH_META.humanities.color})` }}
                                            >
                                                {(p.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-display font-bold text-slate-900">{p.name}</div>
                                                <div className="text-xs text-slate-500">
                                                    Level {p.level} · {p.shared_disciplines.length} shared discipline{p.shared_disciplines.length !== 1 ? "s" : ""}
                                                </div>
                                                <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                                                    {p.why}
                                                </p>
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    {p.shared_disciplines.slice(0, 4).map((d, di) => (
                                                        <span key={di} className="chip text-[10px]" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text, padding: "0.15rem 0.55rem" }}>
                                                            {d}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-3 flex items-center gap-2">
                                            <button
                                                onClick={() => openInvite(p)}
                                                className="arcade-btn"
                                                data-variant="primary"
                                                data-testid={`invite-${p.user_id}`}
                                                style={{ padding: "0.4rem 0.9rem", fontSize: "0.75rem" }}
                                            >
                                                <Send className="w-3 h-3" /> Send collab invite
                                            </button>
                                            {p.username && (
                                                <Link
                                                    to={`/u/${p.username}`}
                                                    className="text-xs text-slate-500 hover:text-slate-900 font-display font-bold"
                                                    data-testid={`match-view-${p.user_id}`}
                                                >
                                                    View ledger →
                                                </Link>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </section>

                        {/* Project ideas */}
                        <section data-testid="matchmaker-projects">
                            <div className="flex items-center gap-2 mb-3">
                                <Lightbulb className="w-4 h-4" color={BRANCH_META.arts.color} />
                                <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                                    Project ideas
                                </h2>
                            </div>
                            <div className="space-y-3">
                                {cache.projects.map((pj, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.05 + i * 0.03 }}
                                        className="crystal rounded-2xl p-5"
                                        data-testid={`match-project-${i}`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: BRANCH_META.arts.soft }}>
                                                <Lightbulb className="w-4 h-4" color={BRANCH_META.arts.color} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-display font-bold text-lg tracking-tight text-slate-900 leading-tight">
                                                    {pj.title}
                                                </h3>
                                                <p className="text-sm text-slate-700 mt-1 leading-relaxed">
                                                    {pj.description}
                                                </p>
                                                {pj.kickoff && (
                                                    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
                                                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                                                            First move
                                                        </div>
                                                        <p className="text-sm text-slate-800">{pj.kickoff}</p>
                                                    </div>
                                                )}
                                                {pj.disciplines?.length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                                        {pj.disciplines.map((d, di) => (
                                                            <span key={di} className="chip text-[10px]" style={{ background: BRANCH_META.trades.soft, color: BRANCH_META.trades.text, padding: "0.15rem 0.55rem" }}>
                                                                {d}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {cache.peers.length > 0 && (
                                                    <button
                                                        onClick={() => openInvite(cache.peers[0], pj.title)}
                                                        className="arcade-btn mt-3"
                                                        data-variant="ghost"
                                                        data-testid={`pitch-${i}`}
                                                        style={{ padding: "0.35rem 0.75rem", fontSize: "0.7rem" }}
                                                    >
                                                        <Send className="w-3 h-3" /> Pitch to {cache.peers[0].name.split(" ")[0]}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </section>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {inviting && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-md flex items-center justify-center p-4"
                        onClick={() => setInviting(null)}
                    >
                        <motion.form
                            onClick={(e) => e.stopPropagation()}
                            onSubmit={sendInvite}
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="crystal rounded-3xl p-6 w-full max-w-md relative"
                        >
                            <button
                                type="button"
                                onClick={() => setInviting(null)}
                                className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"
                                data-testid="invite-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <p className="chip" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}>
                                <Send className="w-3 h-3" /> Collab invite
                            </p>
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-2">
                                Reach out to {inviting.name}
                            </h2>
                            {inviting.project_title && (
                                <p className="text-sm text-slate-600 mt-1">
                                    Pitching: <strong>{inviting.project_title}</strong>
                                </p>
                            )}
                            <textarea
                                autoFocus
                                className="field-input mt-3 min-h-[130px] resize-y"
                                value={inviteMsg}
                                onChange={(e) => setInviteMsg(e.target.value)}
                                maxLength={1000}
                                data-testid="invite-message"
                            />
                            <div className="mt-4 flex justify-end gap-2">
                                <button type="button" onClick={() => setInviting(null)} className="arcade-btn" data-variant="ghost">
                                    Cancel
                                </button>
                                <button className="arcade-btn" data-variant="primary" disabled={busy || !inviteMsg.trim()} data-testid="invite-submit">
                                    <Send className="w-4 h-4" /> Send invite
                                </button>
                            </div>
                        </motion.form>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
